import { AdapterLogo } from "@/features/adapters/components/AdapterLogo";
import Button from "@imminently/decisively-ui/Button";
import DropdownMenu from "@imminently/decisively-ui/DropdownMenu";
import SidebarNav from "@imminently/decisively-ui/SidebarNav";
import SteppedProgress from "@imminently/decisively-ui/SteppedProgress";
import type { Release } from "@packages/commons";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { useCurrentRelease } from "@/common";
import { adapterDefinitions, getAdapterDisplayName, getAdapterPersistenceId, getAnswers, type AdapterApplyAnswers } from "@/features/adapters/model";
import { useAdapterWorkflow } from "@/features/adapters/context/AdapterWorkflowContext";
import { clearAdapterWorkflowOpenState, getAdapterWorkflowOpenState } from "@/features/adapters/openState";
import {
  createAdapterWorkflow,
  getAdapterVisitedSteps,
  getReleaseAdapterWorkflow,
  getAdapterWorkflowStepState,
  sanitizeAdapterAnswers,
  setReleaseAdapterWorkflow,
} from "@/features/adapters/persistence";
import { evaluateAdapterWorkflow, resolveAdapterCurrentStepIndex } from "@/features/adapters/evaluation";
import { buildAdapterWizardSteps } from "@/features/adapters/wizard";
import { cn } from "@/lib/utils";

type AdapterProgressTone = "success" | "warning" | "error";

const progressCircleStroke: Record<AdapterProgressTone, string> = {
  success: "stroke-[var(--success)]",
  warning: "stroke-[var(--warning)]",
  error: "stroke-[var(--destructive)]",
};

const AdapterProgressCircle = ({
  progress,
  tone,
}: {
  progress: number;
  tone: AdapterProgressTone;
}) => {
  const radius = 7;
  const circumference = 2 * Math.PI * radius;
  const normalizedProgress = Math.min(Math.max(progress, 0), 1);

  return (
    <svg
      className="h-[18px] w-[18px] shrink-0 -rotate-90"
      aria-hidden="true"
      data-adapter-progress-state={tone}
      data-adapter-progress-value={Math.round(normalizedProgress * 100)}
      viewBox="0 0 18 18"
    >
      <circle
        className="fill-none stroke-sidebar-foreground/20"
        cx="9"
        cy="9"
        r={radius}
        strokeWidth="2.5"
      />
      <circle
        className={`fill-none transition-[stroke-dashoffset] ${progressCircleStroke[tone]}`}
        cx="9"
        cy="9"
        r={radius}
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - normalizedProgress)}
        strokeLinecap="round"
        strokeWidth="2.5"
      />
    </svg>
  );
};

export const AdapterWorkflowSidebarItem = ({ collapsed = false }: { collapsed?: boolean }) => {
  const release = useCurrentRelease() as Release | undefined;
  const workflow = useAdapterWorkflow();
  const history = useHistory();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!release) return;
    const remembered = getAdapterWorkflowOpenState();
    if (!remembered || remembered.releaseId !== release.id) return;
    const definition = adapterDefinitions.find((candidate) => candidate.id === remembered.definitionId);
    const enabled = release.adapters?.some((adapter) => adapter.format === 2 && adapter.id === getAdapterPersistenceId(remembered.definitionId) && adapter.enabled);
    const persisted = definition ? getReleaseAdapterWorkflow(release, definition.id) : undefined;
    if (!definition || !enabled || !persisted) {
      clearAdapterWorkflowOpenState();
      return;
    }
    if (workflow.releaseId === release.id && workflow.definitionId === definition.id) {
      if (!workflow.isOpen) workflow.openAdapter();
      return;
    }
    const answers = sanitizeAdapterAnswers(definition, getAnswers(release, definition)) as AdapterApplyAnswers;
    workflow.resumeAdapter(release.id, definition.id, answers, persisted);
  }, [release, workflow]);

  if (!release) return null;

  const activeAdapters = adapterDefinitions
    .map((definition) => ({
      definition,
      enabled: release.adapters?.some((adapter) => adapter.format === 2 && adapter.id === getAdapterPersistenceId(definition.id) && adapter.enabled) ?? false,
      progress: getReleaseAdapterWorkflow(release, definition.id),
    }))
    .filter((entry) => entry.enabled && entry.progress);
  if (activeAdapters.length === 0) return null;

  const mostRecentDefinition = activeAdapters
    .sort((left, right) => Date.parse(right.progress!.updatedAt) - Date.parse(left.progress!.updatedAt))[0]?.definition;
  const selectedDefinition =
    workflow.releaseId === release.id
      ? (activeAdapters.find(({ definition }) => definition.id === workflow.definitionId)?.definition ?? mostRecentDefinition)
      : mostRecentDefinition;
  const selectedPersisted = selectedDefinition
    ? getReleaseAdapterWorkflow(release, selectedDefinition.id)
    : undefined;
  const selectedActive = Boolean(
    selectedDefinition && workflow.releaseId === release.id && workflow.definitionId === selectedDefinition.id,
  );
  const selectedAnswers = selectedDefinition
    ? ((selectedActive && workflow.answers
        ? workflow.answers
        : sanitizeAdapterAnswers(selectedDefinition, getAnswers(release, selectedDefinition))) as AdapterApplyAnswers)
    : undefined;
  const triggerLabel = selectedDefinition && selectedAnswers
    ? getAdapterDisplayName(selectedDefinition, selectedAnswers)
    : "Adapters";
  const selectedSteps = selectedDefinition && selectedAnswers
    ? buildAdapterWizardSteps(selectedDefinition, selectedAnswers, release)
    : [];
  const selectedStepKey = selectedActive ? (workflow.stepKey ?? selectedPersisted?.stepKey) : selectedPersisted?.stepKey;
  const selectedKeyedStepIndex = selectedStepKey
    ? selectedSteps.findIndex((step) => step.key === selectedStepKey)
    : -1;
  const selectedStepIndex = Math.min(
    Math.max(
      selectedKeyedStepIndex >= 0
        ? selectedKeyedStepIndex
        : (selectedActive ? workflow.step : selectedPersisted?.step) ?? 0,
      0,
    ),
    Math.max(selectedSteps.length - 1, 0),
  );
  const selectedHasError = Boolean(
    selectedActive && Object.values(workflow.stepErrorsByKey ?? {}).some((errors) => errors.length > 0),
  );
  const selectedStepStatus = selectedActive
    ? (workflow.stepStatus ?? selectedPersisted?.stepStatus ?? [])
    : (selectedPersisted?.stepStatus ?? []);
  const selectedManualCompletions = selectedActive
    ? (workflow.manualStepCompletions ?? {})
    : (selectedPersisted?.manualStepCompletions ?? {});
  const triggerProgressTone: AdapterProgressTone = selectedHasError ||
    selectedStepStatus.some((step) => step.state === "destructive")
    ? "error"
    : selectedStepStatus.some((step) => step.state === "warning")
      ? "warning"
      : "success";
  const selectedCompletedStepCount = selectedSteps.filter(
    (step) => getAdapterWorkflowStepState(selectedStepStatus, step.key) === "success" || selectedManualCompletions[step.key],
  ).length;
  const triggerProgress = selectedPersisted?.status === "complete"
    ? 1
    : selectedSteps.length > 0
      ? selectedCompletedStepCount / selectedSteps.length
      : 0;
  const openGuide = (definition: (typeof adapterDefinitions)[number]) => {
    const persisted = getReleaseAdapterWorkflow(release, definition.id);
    const answers = sanitizeAdapterAnswers(definition, getAnswers(release, definition)) as AdapterApplyAnswers;
    const active = workflow.releaseId === release.id && workflow.definitionId === definition.id;

    if (active) {
      workflow.openAdapter();
      return;
    }
    if (persisted) {
      workflow.resumeAdapter(release.id, definition.id, answers, persisted);
      return;
    }

    const nextWorkflow = createAdapterWorkflow();
    workflow.startAdapter(release.id, definition.id, answers);
    // Through the shared queue, so it cannot overwrite a save that is still landing.
    void workflow
      .saveAdapters((current) => setReleaseAdapterWorkflow(current, definition, nextWorkflow, answers))
      .catch(() => undefined);
  };

  const viewAllAdapters = () => {
    workflow.closeAdapter();
    history.push("/build/adapters");
  };

  const openCurrentAdapter = () => {
    if (!selectedDefinition) return;
    setMenuOpen(false);
    openGuide(selectedDefinition);
  };

  return (
    <SidebarNav.MenuItem>
      <DropdownMenu
        open={menuOpen}
        onOpenChange={setMenuOpen}
      >
        <DropdownMenu.Trigger asChild>
          <SidebarNav.MenuButton
            aria-label={triggerLabel}
            tooltip="Browse adapters"
            collapsed={collapsed}
            className={cn(
              "relative h-auto cursor-pointer bg-sidebar-accent py-1.5 hover:bg-sidebar-accent",
              collapsed ? "justify-center" : "gap-2",
            )}
          >
            {selectedDefinition ? (
              <AdapterProgressCircle
                progress={triggerProgress}
                tone={triggerProgressTone}
              />
            ) : null}
            {collapsed ? null : (
              <span className="min-w-0 leading-tight">
                <span className="block text-[10px] font-medium uppercase tracking-wide text-sidebar-foreground/60">
                  Adapter
                </span>
                <span className="block truncate">{triggerLabel}</span>
              </span>
            )}
          </SidebarNav.MenuButton>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          side="right"
          align="start"
          sideOffset={8}
          className="w-[28rem] max-w-[calc(100vw-2rem)]"
        >
          <DropdownMenu.Label className="grid gap-1 px-2 py-2 normal-case">
            <span className="text-sm font-semibold">Adapters</span>
            <span className="text-xs font-normal text-foreground">
              View an adapter and manage its guided setup.
            </span>
          </DropdownMenu.Label>
          <DropdownMenu.Separator />
          {activeAdapters.map(({ definition }) => {
            const persisted = getReleaseAdapterWorkflow(release, definition.id);
            const active = workflow.releaseId === release.id && workflow.definitionId === definition.id;
            const answers = (active && workflow.answers
              ? workflow.answers
              : sanitizeAdapterAnswers(definition, getAnswers(release, definition))) as AdapterApplyAnswers;
            const steps = buildAdapterWizardSteps(definition, answers, release);
            const adapterName = getAdapterDisplayName(definition, answers);
            const selectedStepKey = active ? (workflow.stepKey ?? persisted?.stepKey) : persisted?.stepKey;
            const selectedStepIndex = resolveAdapterCurrentStepIndex(steps, selectedStepKey, active ? workflow.step : persisted?.step);
            const stepStatus = active
              ? (workflow.stepStatus ?? persisted?.stepStatus ?? [])
              : (persisted?.stepStatus ?? []);
            // Without live resource checks here, a step's completion comes from its saved status.
            const { segments: progressSteps } = evaluateAdapterWorkflow({
              steps,
              definition,
              answers,
              inspections: [],
              confirmations: persisted?.confirmations ?? {},
              currentStepIndex: selectedStepIndex,
              stepStatus,
              visitedSteps: active ? workflow.visitedSteps : getAdapterVisitedSteps(persisted),
              manualStepCompletions: active ? (workflow.manualStepCompletions ?? {}) : (persisted?.manualStepCompletions ?? {}),
              automaticCompletion: Object.fromEntries(steps.map((step) => [
                step.key,
                persisted?.status === "complete" || getAdapterWorkflowStepState(stepStatus, step.key) === "success",
              ])),
              errorsByKey: active ? workflow.stepErrorsByKey : undefined,
              started: Boolean(persisted || active),
            });
            return (
              <div
                key={definition.id}
                className="grid w-full min-w-0 gap-2 px-2 py-2"
              >
                <DropdownMenu.Item onClick={() => openGuide(definition)}>
                  <DropdownMenu.Item.Label className="w-full whitespace-normal">
                    <span className="flex items-center gap-2">
                      <AdapterLogo definition={definition} size="sm" />
                      <span className="text-sm font-semibold">{adapterName}</span>
                    </span>
                  </DropdownMenu.Item.Label>
                </DropdownMenu.Item>
                {persisted || active ? (
                  <div
                    className="grid w-full min-w-0 gap-2"
                    data-adapter-progress="true"
                  >
                    <SteppedProgress
                      ariaLabel={`${adapterName} setup steps`}
                      className="w-full"
                      currentIndex={selectedStepIndex}
                      onSelect={(index) => {
                        const step = steps[index];
                        if (!step) return;
                        setMenuOpen(false);
                        workflow.openAdapterAtStep(definition.id, step.key);
                      }}
                      steps={progressSteps}
                    />
                    <span className="text-xs font-normal text-foreground">
                      Current step: <span className="font-semibold">{steps[selectedStepIndex]?.label}</span>
                    </span>
                  </div>
                ) : (
                  <span className="line-clamp-2 text-xs font-normal leading-4 text-foreground">
                    {definition.description}
                  </span>
                )}
              </div>
            );
          })}
          <DropdownMenu.Separator />
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 p-2">
            <Button
              className="w-full justify-center"
              intent="primary"
              label="Open current"
              onClick={openCurrentAdapter}
            >
              Open current
            </Button>
            <Button
              variant="ghost"
              label="View all"
              onClick={viewAllAdapters}
            >
              View all
            </Button>
          </div>
        </DropdownMenu.Content>
      </DropdownMenu>
    </SidebarNav.MenuItem>
  );
};
